import type React from 'react';
import { useTranslation } from 'react-i18next';
import { formatGel } from '@/utils/money';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { BalancePicture } from '@/api/models';
import { TONE_BY_ZONE, formatRatio } from '../overview/overviewMeta';
import { BALANCE_KEYS, BALANCE_TILES } from './balancePictureMeta';
import { StatTile } from './StatTile';

type BalancePictureViewProps = {
    data: BalancePicture;
};

/**
 * The structural strip — render only.
 *
 * Five tiles in one row: what the business holds, what falls due, the room
 * between, and how fast the two turn over. A−B is printed beside its own
 * operands so a reader can check the subtraction by eye — which is the whole
 * reason the first tile reads *current* assets rather than the mock's "total".
 *
 * The two ratios render only when the server computed them. A cash cycle needs
 * something to have been bought and sold; a GMROI needs stock to hold. Neither
 * is zero when it is absent, so neither shows a dash.
 */
export const BalancePictureView: React.FC<BalancePictureViewProps> = ({
    data,
}) => {
    const { t } = useTranslation('pages');

    return (
        <section
            aria-label={t(BALANCE_KEYS.title)}
            className="flex w-full flex-col gap-4"
            {...getNodeTestIdProp('balance-picture')}
        >
            <div className="flex flex-col gap-1">
                <h3 className="text-base font-semibold text-foreground">
                    {t(BALANCE_KEYS.title)}
                </h3>
                <p className="text-sm text-muted">{t(BALANCE_KEYS.subtitle)}</p>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
                <StatTile
                    href={BALANCE_TILES.currentAssets.href}
                    icon={BALANCE_TILES.currentAssets.icon}
                    label={t(BALANCE_TILES.currentAssets.labelKey)}
                    linkLabel={t(BALANCE_TILES.currentAssets.labelKey)}
                    slot="balance-current-assets"
                    value={formatGel(data.currentAssetsTetri ?? 0)}
                />
                <StatTile
                    href={BALANCE_TILES.currentLiabilities.href}
                    icon={BALANCE_TILES.currentLiabilities.icon}
                    label={t(BALANCE_TILES.currentLiabilities.labelKey)}
                    linkLabel={t(BALANCE_TILES.currentLiabilities.labelKey)}
                    slot="balance-current-liabilities"
                    value={formatGel(data.currentLiabilitiesTetri ?? 0)}
                />
                <StatTile
                    icon={BALANCE_TILES.workingCapital.icon}
                    label={t(BALANCE_TILES.workingCapital.labelKey)}
                    slot="balance-working-capital"
                    tone={
                        data.workingCapitalTone
                            ? TONE_BY_ZONE[data.workingCapitalTone]
                            : null
                    }
                    value={formatGel(data.workingCapitalTetri ?? 0)}
                />
                {data.cashCycle.state === 'COMPUTED' &&
                data.cashCycle.tone !== null ? (
                    <StatTile
                        icon={BALANCE_TILES.cashCycle.icon}
                        label={t(BALANCE_TILES.cashCycle.labelKey)}
                        slot="balance-cash-cycle"
                        value={t(BALANCE_KEYS.days, {
                            count: data.cashCycle.days ?? 0,
                        })}
                        verdict={{
                            label: t(BALANCE_KEYS.verdict[data.cashCycle.tone]),
                            tone: TONE_BY_ZONE[data.cashCycle.tone],
                        }}
                    />
                ) : null}
                {data.gmroi.state === 'COMPUTED' && data.gmroi.tone !== null ? (
                    <StatTile
                        href={BALANCE_TILES.gmroi.href}
                        icon={BALANCE_TILES.gmroi.icon}
                        label={t(BALANCE_TILES.gmroi.labelKey)}
                        linkLabel={t(BALANCE_TILES.gmroi.labelKey)}
                        slot="balance-gmroi"
                        value={t(BALANCE_KEYS.times, {
                            value: formatRatio(data.gmroi.value ?? 0),
                        })}
                        verdict={{
                            label: t(
                                BALANCE_KEYS.gmroiVerdict[data.gmroi.tone],
                            ),
                            tone: TONE_BY_ZONE[data.gmroi.tone],
                        }}
                    />
                ) : null}
            </div>
        </section>
    );
};
